import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GUESTRIDE_STATUS, type GuestrideStatus } from '../constants/guestRideConstants';

// ─── Types ──────────────────────────────────────────────────────────────────

export type GuestRideStatusFilter = 'all' | 'active' | 'upcoming' | 'completed' | 'deleted';
export type GuestRideSortBy = 'created_at' | 'guest_name' | 'status' | 'pickup_address' | 'dropoff_address' | 'cost';
export type SortOrder = 'asc' | 'desc';
export type GuestRideViewMode = 'list' | 'grid' | 'map';
export type DateRangePreset = 'today' | 'yesterday' | 'thisweek' | 'lastweek' | 'thismonth' | 'lastmonth' | 'custom';

export interface GuestRideFilters {
  statusFilter: GuestRideStatusFilter;
  search: string;
  dateRange: DateRangePreset;
  startDate: string;
  endDate: string;
}

// Optimistic status overlay: when a dealer calls (or retries) a ride we assume
// success and render it as `pending` straight away, so the Call/Retry button
// stops lingering and inviting a double-click while the server catches up.
// `baseline` is the ride's real status at call time; the merge in useGuestRides
// drops the overlay as soon as the server moves off that baseline (success →
// pending/booked, or failure → failed), and a TTL backstop clears it if the
// server never moves, so a silently-failed call brings the button back.
export interface OptimisticRideStatus {
  status: GuestrideStatus;
  baseline: string;
  at: number;
}

interface GuestRideStoreState {
  filters: GuestRideFilters;
  page: number;
  pageSize: number;
  sortBy: GuestRideSortBy;
  sortOrder: SortOrder;
  viewMode: GuestRideViewMode;

  // UI state
  highlightedRideId: number | null;
  optimisticStatuses: Record<number, OptimisticRideStatus>;

  // Actions
  setFilters: (filters: Partial<GuestRideFilters>) => void;
  updateFilter: <K extends keyof GuestRideFilters>(key: K, value: GuestRideFilters[K]) => void;
  resetFilters: () => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSorting: (sortBy: GuestRideSortBy, sortOrder: SortOrder) => void;
  setViewMode: (mode: GuestRideViewMode) => void;
  setHighlightedRideId: (id: number | null) => void;
  markRideCalled: (id: number, baseline: string) => void;
  clearOptimisticStatus: (id: number) => void;
}

// ─── Defaults ───────────────────────────────────────────────────────────────

const defaultFilters: GuestRideFilters = {
  statusFilter: 'all',
  search: '',
  dateRange: 'today',
  startDate: '',
  endDate: '',
};

const defaultState = {
  filters: defaultFilters,
  page: 0,
  pageSize: 25,
  sortBy: 'created_at' as GuestRideSortBy,
  sortOrder: 'desc' as SortOrder,
  viewMode: 'list' as GuestRideViewMode,
  highlightedRideId: null as number | null,
  optimisticStatuses: {} as Record<number, OptimisticRideStatus>,
};

// ─── Store ──────────────────────────────────────────────────────────────────

export const useGuestRideStore = create<GuestRideStoreState>()(
  persist(
    (set) => ({
      ...defaultState,

      setFilters: (newFilters) =>
        set((state) => ({
          filters: { ...state.filters, ...newFilters },
          page: 0,
        })),

      updateFilter: (key, value) =>
        set((state) => ({
          filters: { ...state.filters, [key]: value },
          page: 0,
        })),

      resetFilters: () => set({ filters: defaultFilters, page: 0 }),

      setPage: (page) => set({ page }),

      setPageSize: (pageSize) => set({ pageSize, page: 0 }),

      setSorting: (sortBy, sortOrder) => set({ sortBy, sortOrder, page: 0 }),

      setViewMode: (viewMode) => set({ viewMode }),

      setHighlightedRideId: (highlightedRideId) => set({ highlightedRideId }),

      markRideCalled: (id, baseline) =>
        set((state) => ({
          optimisticStatuses: {
            ...state.optimisticStatuses,
            [id]: { status: GUESTRIDE_STATUS.PENDING, baseline, at: Date.now() },
          },
        })),

      clearOptimisticStatus: (id) =>
        set((state) => {
          if (!(id in state.optimisticStatuses)) return state;
          const next = { ...state.optimisticStatuses };
          delete next[id];
          return { optimisticStatuses: next };
        }),
    }),
    {
      name: 'guestride-view-settings',
      partialize: (state) => ({
        filters: state.filters,
        pageSize: state.pageSize,
        sortBy: state.sortBy,
        sortOrder: state.sortOrder,
        viewMode: state.viewMode,
      }),
    },
  ),
);
